import React, { useEffect, useState, useMemo } from 'react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { Alert, AlertDescription } from '@roger/ui-components';
import {
  Search,
  Download,
  Loader2,
  AlertTriangle,
  FileSpreadsheet,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
} from 'lucide-react';
import ExcelJS from 'exceljs';
import { getCompletedVisits } from '../services/rogerDocApi';
import { getTodayInPT } from '../utils/dateUtils';
import { downloadBlobAsFile } from '../utils/downloadFile';
import { VISIT_TYPE_LABELS } from '../constants/visitTypeLabels';
import {
  DEFAULT_USAGE_SORT,
  nextUsageSort,
  sortUsageRows,
  USAGE_SORT_LABELS,
  type UsageSortColumn,
  type UsageSortConfig,
  type UsageSortDirection,
  type UsageTimeRange,
} from './rogerDocUsageSort';

const PAGE_SIZE = 10;

async function downloadWorkbook(
  workbook: ExcelJS.Workbook,
  filename: string
): Promise<void> {
  const buffer = await workbook.xlsx.writeBuffer();
  downloadBlobAsFile(
    buffer,
    filename,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  );
}

const SortIcon: React.FC<{
  direction: UsageSortDirection | null;
}> = React.memo(function SortIcon({ direction }) {
  if (direction === 'asc') {
    return <ArrowUp className='h-3.5 w-3.5' aria-hidden='true' />;
  }
  if (direction === 'desc') {
    return <ArrowDown className='h-3.5 w-3.5' aria-hidden='true' />;
  }
  return <ArrowUpDown className='h-3.5 w-3.5' aria-hidden='true' />;
});

const SortableTableHead: React.FC<{
  column: UsageSortColumn;
  sort: UsageSortConfig;
  onSort: (column: UsageSortColumn) => void;
  align?: 'left' | 'right';
}> = React.memo(function SortableTableHead({
  column,
  sort,
  onSort,
  align = 'left',
}) {
  const label = USAGE_SORT_LABELS[column];
  const isActive = sort.column === column;
  const direction = isActive ? sort.direction : null;

  return (
    <TableHead
      className={align === 'right' ? 'text-right' : undefined}
      aria-label={label}
      aria-sort={
        isActive
          ? sort.direction === 'asc'
            ? 'ascending'
            : 'descending'
          : 'none'
      }
    >
      <button
        type='button'
        className={`inline-flex h-8 items-center gap-1 whitespace-nowrap rounded font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
          align === 'right'
            ? 'ml-auto flex-row-reverse text-right'
            : 'text-left'
        }`}
        aria-label={`Sort by ${label}`}
        onClick={() => onSort(column)}
      >
        <span>{label}</span>
        <SortIcon direction={direction} />
      </button>
    </TableHead>
  );
});

const RogerDocUsage: React.FC = () => {
  const {
    hasPermission,
    usageStats,
    usageCalculatedAt,
    usageStatsLoading,
    fetchUsageStats,
    showError,
  } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
      usageStats: state.usageStats,
      usageCalculatedAt: state.usageCalculatedAt,
      usageStatsLoading: state.usageStatsLoading,
      fetchUsageStats: state.fetchUsageStats,
      showError: state.showError,
    }))
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [timeRange, setTimeRange] = useState<UsageTimeRange>('all_time');
  const [currentPage, setCurrentPage] = useState(1);
  const [sort, setSort] = useState<UsageSortConfig>(DEFAULT_USAGE_SORT);
  const [exportingAllVisits, setExportingAllVisits] = useState(false);

  const canManage = hasPermission('rogerdoc-dashboard:manage');

  useEffect(() => {
    if (canManage) {
      // The slice already records the error for the UI.
      fetchUsageStats().catch(() => {});
    }
  }, [canManage, fetchUsageStats]);

  // Filter by search term
  const filteredUsage = useMemo(() => {
    if (!usageStats) return [];
    const term = searchTerm.toLowerCase();
    return usageStats.filter(
      user =>
        user.first_name?.toLowerCase().includes(term) ||
        user.last_name?.toLowerCase().includes(term) ||
        user.username?.toLowerCase().includes(term)
    );
  }, [usageStats, searchTerm]);

  // Sort the full filtered set, then paginate.
  const sortedUsage = useMemo(
    () => sortUsageRows(filteredUsage, sort, timeRange),
    [filteredUsage, sort, timeRange]
  );

  const sortedAndPaginatedUsage = useMemo(
    () =>
      sortedUsage.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [sortedUsage, currentPage]
  );

  const totalPages = Math.ceil(filteredUsage.length / PAGE_SIZE);

  // Reset to page 1 when search or data changes (sort/timeRange reset inline).
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, usageStats]);

  const calculatedDate = usageCalculatedAt
    ? new Date(usageCalculatedAt).toLocaleDateString()
    : '';

  const handleSort = (column: UsageSortColumn) => {
    setSort(current => nextUsageSort(current, column));
    setCurrentPage(1);
  };

  const handleTimeRangeChange = (value: string) => {
    setTimeRange(value as UsageTimeRange);
    setCurrentPage(1);
  };

  const handleExportAllVisits = async () => {
    setExportingAllVisits(true);
    try {
      const visits = await getCompletedVisits('2023-01-01', getTodayInPT());

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Completed Visits');

      // Only include Role column if any visit has role data
      const hasRoleData = visits.some(v => v.clinician_role);

      const columns: Partial<ExcelJS.Column>[] = [
        { header: 'MRN', key: 'mrn', width: 15 },
        { header: 'Clinician', key: 'clinician', width: 25 },
        ...(hasRoleData ? [{ header: 'Role', key: 'role', width: 20 }] : []),
        { header: 'Visit Type', key: 'visitType', width: 25 },
        { header: 'Target Date', key: 'targetDate', width: 15 },
        { header: 'Completed Date', key: 'completedDate', width: 18 },
      ];
      worksheet.columns = columns;

      for (const visit of visits) {
        worksheet.addRow({
          mrn: visit.mrn ?? '',
          clinician: visit.clinician_name ?? '',
          ...(hasRoleData ? { role: visit.clinician_role ?? '' } : {}),
          visitType:
            VISIT_TYPE_LABELS[visit.visit_type ?? ''] ?? visit.visit_type ?? '',
          targetDate: visit.target_date ?? '',
          completedDate: visit.completed_date ?? '',
        });
      }

      // Enable auto-filter on all columns (full data range for LibreOffice compat)
      worksheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: visits.length + 1, column: columns.length },
      };

      // Bold header row
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };

      await downloadWorkbook(
        workbook,
        `Roger_completed_visits_${getTodayInPT()}.xlsx`
      );
    } catch (error) {
      console.error('Failed to export completed visits:', error);
      showError('Failed to export completed visits');
    } finally {
      setExportingAllVisits(false);
    }
  };

  const handleExportExcel = async () => {
    if (!usageStats || usageStats.length === 0) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    // Define columns
    worksheet.columns = [
      { header: 'ClinicianName', key: 'ClinicianName', width: 20 },
      { header: 'Title', key: 'Title', width: 15 },
      { header: 'TotalVisits_overall', key: 'TotalVisits_overall', width: 20 },
      { header: 'OasisVisits_overall', key: 'OasisVisits_overall', width: 20 },
      {
        header: 'RoutineVisits_overall',
        key: 'RoutineVisits_overall',
        width: 22,
      },
      {
        header: 'AvgOasisTimeSaving_min_overall',
        key: 'AvgOasisTimeSaving_min_overall',
        width: 30,
      },
      {
        header: 'AvgRoutineTimeSaving_min_overall',
        key: 'AvgRoutineTimeSaving_min_overall',
        width: 33,
      },
      { header: 'TotalVisits_30days', key: 'TotalVisits_30days', width: 20 },
      { header: 'OasisVisits_30days', key: 'OasisVisits_30days', width: 20 },
      {
        header: 'RoutineVisits_30days',
        key: 'RoutineVisits_30days',
        width: 22,
      },
      {
        header: 'AvgOasisTimeSaving_min_30days',
        key: 'AvgOasisTimeSaving_min_30days',
        width: 30,
      },
      {
        header: 'AvgRoutineTimeSaving_min_30days',
        key: 'AvgRoutineTimeSaving_min_30days',
        width: 33,
      },
      { header: 'TotalVisits_60days', key: 'TotalVisits_60days', width: 20 },
      { header: 'OasisVisits_60days', key: 'OasisVisits_60days', width: 20 },
      {
        header: 'RoutineVisits_60days',
        key: 'RoutineVisits_60days',
        width: 22,
      },
      {
        header: 'AvgOasisTimeSaving_min_60days',
        key: 'AvgOasisTimeSaving_min_60days',
        width: 30,
      },
      {
        header: 'AvgRoutineTimeSaving_min_60days',
        key: 'AvgRoutineTimeSaving_min_60days',
        width: 33,
      },
    ];

    // Add rows
    usageStats.forEach(row => {
      worksheet.addRow({
        ClinicianName: `${row.first_name} ${row.last_name}`,
        Title: row.clinician_title,
        TotalVisits_overall: row.all_time.total_visits,
        OasisVisits_overall: row.all_time.oasis_visits,
        RoutineVisits_overall: row.all_time.routine_visits,
        AvgOasisTimeSaving_min_overall: row.all_time.avg_oasis_time_saving,
        AvgRoutineTimeSaving_min_overall: row.all_time.avg_routine_time_saving,
        TotalVisits_30days: row['30_days'].total_visits,
        OasisVisits_30days: row['30_days'].oasis_visits,
        RoutineVisits_30days: row['30_days'].routine_visits,
        AvgOasisTimeSaving_min_30days: row['30_days'].avg_oasis_time_saving,
        AvgRoutineTimeSaving_min_30days: row['30_days'].avg_routine_time_saving,
        TotalVisits_60days: row['60_days'].total_visits,
        OasisVisits_60days: row['60_days'].oasis_visits,
        RoutineVisits_60days: row['60_days'].routine_visits,
        AvgOasisTimeSaving_min_60days: row['60_days'].avg_oasis_time_saving,
        AvgRoutineTimeSaving_min_60days: row['60_days'].avg_routine_time_saving,
      });
    });

    // Enable auto-filter on all columns
    worksheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: worksheet.columns.length },
    };

    // Bold header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };

    // Generate filename with date
    const dateObj = usageCalculatedAt
      ? new Date(usageCalculatedAt)
      : new Date();
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const filename = `Roger_usage_${year}-${month}-${day}.xlsx`;

    await downloadWorkbook(workbook, filename);
  };

  if (!canManage) {
    return (
      <div>
        <Alert className='border-red-200 bg-red-50'>
          <AlertTriangle className='h-4 w-4 text-red-600' />
          <AlertDescription className='text-red-800'>
            You don't have permission to access Roger App Dashboard features.
            Please contact your administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between flex-wrap gap-4'>
            <CardTitle>
              Visits Completed with Roger
              {calculatedDate && ` (updated ${calculatedDate})`}
            </CardTitle>
            <div className='flex gap-2 flex-wrap'>
              <Select value={timeRange} onValueChange={handleTimeRangeChange}>
                <SelectTrigger className='w-[140px]'>
                  <SelectValue placeholder='Select range' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all_time'>Overall</SelectItem>
                  <SelectItem value='30_days'>30 Days</SelectItem>
                  <SelectItem value='60_days'>60 Days</SelectItem>
                </SelectContent>
              </Select>
              <div className='relative w-64'>
                <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                <Input
                  placeholder='Search by name...'
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className='pl-10'
                />
              </div>
              <Button
                variant='outline'
                onClick={handleExportExcel}
                disabled={!usageStats || usageStats.length === 0}
              >
                <Download className='h-4 w-4' />
                Download
              </Button>
              <Button
                variant='outline'
                onClick={handleExportAllVisits}
                disabled={exportingAllVisits}
              >
                {exportingAllVisits ? (
                  <Loader2 className='h-4 w-4 animate-spin' />
                ) : (
                  <FileSpreadsheet className='h-4 w-4' />
                )}
                Export All Visits
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {usageStatsLoading ? (
            <div className='flex items-center justify-center py-12'>
              <div className='text-center space-y-3'>
                <Loader2 className='h-8 w-8 animate-spin text-primary mx-auto' />
                <p className='text-muted-foreground'>
                  Calculating usage report... this can take a couple of minutes
                </p>
              </div>
            </div>
          ) : sortedAndPaginatedUsage.length > 0 ? (
            <>
              <Table className='text-base'>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead
                      column='full_name'
                      sort={sort}
                      onSort={handleSort}
                    />
                    <SortableTableHead
                      column='title'
                      sort={sort}
                      onSort={handleSort}
                    />
                    <SortableTableHead
                      column='total_visits'
                      sort={sort}
                      onSort={handleSort}
                      align='right'
                    />
                    <SortableTableHead
                      column='oasis_visits'
                      sort={sort}
                      onSort={handleSort}
                      align='right'
                    />
                    <SortableTableHead
                      column='routine_visits'
                      sort={sort}
                      onSort={handleSort}
                      align='right'
                    />
                    <SortableTableHead
                      column='oasis_savings'
                      sort={sort}
                      onSort={handleSort}
                      align='right'
                    />
                    <SortableTableHead
                      column='routine_savings'
                      sort={sort}
                      onSort={handleSort}
                      align='right'
                    />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedAndPaginatedUsage.map(user => {
                    const timeRangeData = user[timeRange];
                    return (
                      <TableRow
                        key={`${user.first_name}-${user.last_name}-${user.username}`}
                      >
                        <TableCell>
                          {user.first_name} {user.last_name}
                        </TableCell>
                        <TableCell>{user.clinician_title}</TableCell>
                        <TableCell className='text-right'>
                          {timeRangeData.total_visits}
                        </TableCell>
                        <TableCell className='text-right'>
                          {timeRangeData.oasis_visits}
                        </TableCell>
                        <TableCell className='text-right'>
                          {timeRangeData.routine_visits}
                        </TableCell>
                        <TableCell className='text-right'>
                          {parseFloat(
                            timeRangeData.avg_oasis_time_saving.toString()
                          ).toFixed(1)}
                        </TableCell>
                        <TableCell className='text-right'>
                          {parseFloat(
                            timeRangeData.avg_routine_time_saving.toString()
                          ).toFixed(1)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className='flex items-center justify-between pt-4 mt-4 border-t'>
                  <div className='text-sm text-muted-foreground'>
                    {(currentPage - 1) * PAGE_SIZE + 1}-
                    {Math.min(currentPage * PAGE_SIZE, filteredUsage.length)} of{' '}
                    {filteredUsage.length} ({usageStats.length} Total)
                  </div>
                  <div className='flex gap-2'>
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <div className='flex items-center gap-2'>
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(
                          page =>
                            page === 1 ||
                            page === totalPages ||
                            (page >= currentPage - 1 && page <= currentPage + 1)
                        )
                        .map((page, idx, arr) => (
                          <React.Fragment key={page}>
                            {idx > 0 && arr[idx - 1] !== page - 1 && (
                              <span className='text-muted-foreground'>...</span>
                            )}
                            <Button
                              size='sm'
                              variant={
                                currentPage === page ? 'default' : 'outline'
                              }
                              onClick={() => setCurrentPage(page)}
                            >
                              {page}
                            </Button>
                          </React.Fragment>
                        ))}
                    </div>
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={() =>
                        setCurrentPage(p => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className='text-center py-12 text-muted-foreground'>
              No usage data found
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RogerDocUsage;
